import React, { useEffect, useState } from 'react';

import { Stack } from '@mantine/core';
import { SetupPaymentMethodForm } from 'components/payment-methods/SetupPaymentMethod';
import { notificationHandler } from 'utils/notifications';

import { useCheckout } from '../../../CheckoutContext';

interface AddPaymentMethodProps {
  onDone: () => void;
}

const AddPaymentMethod = ({ onDone }: AddPaymentMethodProps): JSX.Element => {
  const { form, completeStep, loggedInAthleteFullName, loggedInAthleteEmail } = useCheckout();
  const { errorNotification, successNotification } = notificationHandler();
  const [pendingConfirmationToken, setPendingConfirmationToken] = useState<string | null>(null);

  const storedConfirmationToken = form.values.paymentMethod.stripeConfirmationToken;

  // `form.setFieldValue` is asynchronous, and `completeStep` validates against `form.values`
  // from the current render, so completing the step in the same tick as the write sees a
  // null token and silently refuses. Complete it once the form reflects the new token.
  useEffect(() => {
    if (!pendingConfirmationToken || storedConfirmationToken !== pendingConfirmationToken) return;
    setPendingConfirmationToken(null);
    onDone();
    completeStep('payment');
  }, [pendingConfirmationToken, storedConfirmationToken, onDone, completeStep]);

  const handleSuccess = (confirmationTokenId: string): void => {
    form.setFieldValue('paymentMethod.selectedPaymentMethodId', null);
    form.setFieldValue('paymentMethod.stripeConfirmationToken', confirmationTokenId);

    successNotification(
      'Payment method details collected',
      'Your payment method will be processed during the checkout.',
    );
    setPendingConfirmationToken(confirmationTokenId);
  };

  const handleCancel = (): void => {
    form.setFieldValue('paymentMethod.stripeConfirmationToken', null);
    onDone();
  };

  const handleError = (error: string): void => {
    form.setFieldValue('paymentMethod.stripeConfirmationToken', null);
    errorNotification('The payment method details could not be collected', error);
  };

  return (
    <Stack>
      <SetupPaymentMethodForm
        athleteEmail={loggedInAthleteEmail}
        athleteFullName={loggedInAthleteFullName}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        onError={handleError}
        paymentMethodMetadata={{
          created_via_sales_portal: 'true',
        }}
      />
    </Stack>
  );
};

export default AddPaymentMethod;
